// An answer whose payload is exactly one image and one audio track carries one piece of media,
// not two: cover art and the track it illustrates. They combine into a single tile — the
// transport sits on the artwork's bottom edge over a scrim instead of stacking a 168px player
// below it — in the answer itself and anywhere that answer is rendered, including inside
// another post's embed.
//
// The predicate lives here, not in PayloadContainer, because the height estimator
// (lib/answerPlaceholderHeight) has to reach the same verdict from a geometry projection.
// Renderer and estimator disagreeing on this is a scroll-position bug, not a style bug.

export const COMBINED_MEDIA_FEATURE = 'combined_media'
export const COMBINED_MEDIA_AUDIO_COMPACT_MAX_WIDTH_PX = 300

export function combinedMediaAudioScrimIsCompact(width: number): boolean {
    return Number.isFinite(width) && width > 0 && width <= COMBINED_MEDIA_AUDIO_COMPACT_MAX_WIDTH_PX
}

export function combinedMediaAudioBadgePresentation({
    isPlaying,
    isDockOwned,
    durationLabel,
    elapsedLabel,
}: {
    isPlaying: boolean
    isDockOwned: boolean
    durationLabel: string | null
    elapsedLabel: string | null
}): { live: boolean; label: string | null } {
    const live = isPlaying || isDockOwned
    return {
        live,
        label: live && elapsedLabel ? elapsedLabel : durationLabel,
    }
}

export function combinedMediaEnabled(): boolean {
    return true
}

type FusableItem = {
    kind?: string | null
    status?: string | null
    url?: string | null
    render?: unknown
}

const COMPLETE_STATUSES = new Set(['succeeded', 'finished', 'complete', 'completed', 'done'])

// Some attachment callers carry no status, so a missing value means the media is already
// usable. Explicit in-progress or failed states must not fuse even when they have a URL.
function isCompleteForFusion(status?: string | null): boolean {
    if (!status) return true
    return COMPLETE_STATUSES.has(String(status).toLowerCase())
}

export function combinedMediaItemReady(item: FusableItem | null | undefined): boolean {
    return !!item && !!(item.url || item.render) && isCompleteForFusion(item.status)
}

/**
 * Resolve the fused pair from already-kind-classified payload items. `resolveKind` lets the
 * renderer pass its own mediaKindFromUrl (which corrects mislabeled .m4a files) while the
 * estimator passes projection kinds straight through.
 */
export function combinedMediaPair<T extends FusableItem>(
    items: T[] | null | undefined,
    resolveKind: (item: T) => string | null | undefined = item => item.kind,
): { image: T; audio: T } | null {
    if (!Array.isArray(items) || items.length !== 2) return null

    let image: T | null = null
    let audio: T | null = null

    for (const item of items) {
        // A still-generating tile renders a status frame, not media — nothing to dock onto.
        if (!combinedMediaItemReady(item)) return null

        const kind = resolveKind(item)
        if (kind === 'image' && !image) image = item
        else if (kind === 'audio' && !audio) audio = item
        else return null
    }

    return image && audio ? { image, audio } : null
}

/**
 * PayloadContainer normally splits visual and audio items into separate rows. A combined pair
 * must stay in one row long enough to reach MediaContainer, or neither half can see the other.
 */
export function combinedMediaRow<T extends FusableItem>(
    items: T[] | null | undefined,
    resolveKind: (item: T) => string | null | undefined = item => item.kind,
): { kind: 'combined'; items: T[] } | null {
    if (!combinedMediaEnabled() || !Array.isArray(items)) return null
    return combinedMediaPair(items, resolveKind) ? { kind: 'combined', items } : null
}

/**
 * Estimator-side verdict. Takes the values the estimator has already computed rather than
 * re-deriving them from the projection, so there is exactly one definition of "one image tile
 * plus one audio output" and the two sides cannot drift.
 */
export function projectionCombinesMedia({
    visualTiles,
    audioOutputCount = 0,
    audioReadyOutputCount = audioOutputCount,
    hasRecording = false,
    hasSingleImage = false,
    otherOutputCount = 0,
}: {
    visualTiles: Array<{ kind?: string | null; ready?: boolean }> | null | undefined
    audioOutputCount?: number
    audioReadyOutputCount?: number
    hasRecording?: boolean
    hasSingleImage?: boolean
    otherOutputCount?: number
}): boolean {
    if (!combinedMediaEnabled()) return false
    if (
        audioOutputCount !== 1 ||
        audioReadyOutputCount !== 1 ||
        hasRecording ||
        otherOutputCount > 0
    ) {
        return false
    }

    // Legacy answer_image_url media is projected outside visualTiles, but PayloadContainer
    // still turns it into an ordinary image item. Count it as the same image candidate so the
    // renderer and estimator agree for old posts too.
    const projectedVisuals = [
        ...(hasSingleImage ? [{ kind: 'image' }] : []),
        ...(Array.isArray(visualTiles) ? visualTiles : []),
    ]
    if (projectedVisuals.length !== 1) return false
    return (
        projectedVisuals[0]?.ready !== false && (projectedVisuals[0]?.kind || 'image') === 'image'
    )
}
